import {CSSProperties, FC, ReactNode} from 'react'
import clsx from 'clsx'
import {QRCode} from 'react-qrcode-logo'
import {InvoicePrintModel, InvoicePrintParty} from '../../../invoice-print/core/_models'
import {formatCell, PrintColumn} from '../../../invoice-print/core/printColumns'
import {formatNumber} from '../../../invoice-print/core/printNumbers'
import {
  notices,
  PROVISIONAL_MARK,
  RowsTotals,
  showsPartyName,
  TotalRow,
} from '../../../invoice-print/core/printModel'
import {hasLatinLetters, persianText} from '../../../invoice-print/core/printText'
import {TAX_PUBLIC_QR_CAPTION, taxPublicQrValue} from '../../../invoice-print/core/taxPublicLink'
import {getAttachmentFileUrl} from '../../../../../../../_metronic/helpers/components/AttachmentFileAccess'
import {
  ClassicField,
  ClassicNoteLine,
  classicSubtitle,
  classicTitle,
  columnUnit,
  invalidityNote,
  partyFields,
  showsRegisteredEmblem,
  totalsLabelSpan,
} from './classicPrintModel'
import {TAX_REGISTERED_EMBLEM} from './classicImages'

// The parts of the earlier print page's A4 sheets, in its own arrangement: the header with the logo,
// title and QR code, the seller and buyer boxes with their side boxes, the rows' table with the
// totals under its columns, then payments, description, notices and signatures. The measuring copy
// renders the very same components (ClassicDocument), so what is measured is what prints.

export const FOOT_LABELS = {page: 'جمع این صفحه', running: 'جمع کل تا این صفحه'}

/** An identifier read left to right; with Latin letters (tax IDs, passports) in the Latin face. */
export const Code: FC<{value: string | null | undefined; className?: string}> = ({
  value,
  className,
}) => (
  <bdi dir='ltr' className={clsx('cp-code', {'cp-code--latin': hasLatinLetters(value)}, className)}>
    {value || ''}
  </bdi>
)

/** An amount with Persian separators, isolated from the text around it. */
export const Num: FC<{value: string | null | undefined; className?: string}> = ({
  value,
  className,
}) => (
  <bdi dir='ltr' className={clsx('cp-num', className)}>
    {formatNumber(value)}
  </bdi>
)

const FieldValue: FC<{field: ClassicField}> = ({field}) => {
  switch (field.format) {
    case 'code':
      return <Code value={field.value} />
    case 'amount':
      return <Num value={field.value} />
    case 'date':
      return <bdi className='cp-date'>{field.value}</bdi>
    default:
      return <>{persianText(field.value)}</>
  }
}

const Field: FC<{field: ClassicField; className?: string}> = ({field, className}) => (
  <div className={clsx('cp-field', className)}>
    <span className='cp-field__label'>{field.title}:</span>{' '}
    <span className='cp-field__value'>
      <FieldValue field={field} />
    </span>
  </div>
)

const SideRow: FC<{label: ReactNode; children: ReactNode}> = ({label, children}) => (
  <div className='cp-side__row'>
    <span className='cp-field__label'>{label}:</span>{' '}
    <span className='cp-field__value'>{children}</span>
  </div>
)

/** Every part of a sheet's body sits in one of these; its bottom padding is the gap to the next. */
export const BlockFrame: FC<{measureKey?: string; children: ReactNode}> = ({
  measureKey,
  children,
}) => (
  <div className='cp-block' data-measure-block={measureKey}>
    {children}
  </div>
)

// ---------------------------------------------------------------- header and footer

export const ClassicHeadFull: FC<{
  invoice: InvoicePrintModel
  qrValue: string
  qrLogo: string
  qrId: string
}> = ({invoice, qrValue, qrLogo, qrId}) => {
  const invalid = invalidityNote(invoice)
  const taxQrValue = taxPublicQrValue(invoice)
  return (
    <header className='cp-head'>
      <div className='cp-head__logo'>{invoice.logoUrl && <img src={invoice.logoUrl} alt='' />}</div>
      <div className='cp-head__titles'>
        <h1 className='cp-head__title'>{classicTitle(invoice)}</h1>
        <div className='cp-head__subtitle'>{classicSubtitle(invoice)}</div>
        {invalid && <div className='cp-head__invalid'>{invalid}</div>}
      </div>
      <div className='cp-head__codes'>
        {showsRegisteredEmblem(invoice) && (
          <img className='cp-head__emblem' src={TAX_REGISTERED_EMBLEM} alt='' />
        )}
        {taxQrValue && (
          <div className='cp-head__qr cp-head__qr--tax'>
            <QRCode id={`${qrId}-tax`} value={taxQrValue} size={256} quietZone={6} ecLevel='L' />
            <div className='cp-head__qr-caption'>{TAX_PUBLIC_QR_CAPTION}</div>
          </div>
        )}
        <div className='cp-head__qr'>
          <QRCode
            id={qrId}
            value={qrValue}
            size={256}
            quietZone={6}
            logoImage={qrLogo || undefined}
            removeQrCodeBehindLogo
            logoPadding={2}
            logoPaddingStyle='square'
          />
        </div>
      </div>
    </header>
  )
}

export const ClassicHeadCompact: FC<{invoice: InvoicePrintModel}> = ({invoice}) => (
  <header className='cp-head-compact'>
    <span className='cp-head-compact__title'>صورتحساب الکترونیکی (ادامه)</span>
    {invoice.taxId && (
      <span className='cp-head-compact__item'>
        <span className='cp-field__label'>شماره منحصر به فرد مالیاتی:</span>{' '}
        <Code value={invoice.taxId} />
      </span>
    )}
    {invoice.invoiceNumber && (
      <span className='cp-head-compact__item'>
        <span className='cp-field__label'>شماره صورتحساب:</span>{' '}
        <Code value={invoice.invoiceNumber} />
      </span>
    )}
    <span className='cp-head-compact__item'>
      <span className='cp-field__label'>تاریخ و زمان صدور:</span>{' '}
      <bdi className='cp-date'>{invoice.issuedAt}</bdi>
    </span>
  </header>
)

export const ClassicFoot: FC<{
  invoice: InvoicePrintModel
  page: number
  pages: number
  site: string
}> = ({invoice, page, pages, site}) => (
  <footer className='cp-foot'>
    <span className='cp-foot__page'>
      صفحهٔ {page} از {pages}
    </span>
    <span className='cp-foot__site'>
      صورتحساب الکترونیکی تولید شده در <bdi className='cp-foot__brand'>{site}</bdi>
    </span>
    <span className='cp-foot__id'>
      {invoice.taxId ? (
        <>
          شماره منحصر به فرد مالیاتی <Code value={invoice.taxId} />
        </>
      ) : (
        <>
          کد پیگیری سامانه <Code value={invoice.trackingCode} />
        </>
      )}
    </span>
  </footer>
)

// ---------------------------------------------------------------- seller, buyer and pattern

const PartyBox: FC<{party: InvoicePrintParty | null; empty: string}> = ({party, empty}) => {
  if (!party) {
    return (
      <div className='cp-box cp-party cp-party--empty'>
        <p className='cp-party__empty'>{empty}</p>
      </div>
    )
  }
  const fields = partyFields(party)
  return (
    <div className='cp-box cp-party'>
      <div className='cp-party__main'>
        {showsPartyName(party) && (
          <div className='cp-party__name'>{party.name ? persianText(party.name) : '—'}</div>
        )}
        {party.address && <div className='cp-party__address'>{persianText(party.address)}</div>}
        {party.note && <div className='cp-party__note'>{persianText(party.note)}</div>}
      </div>
      {fields.length > 0 && (
        <div className='cp-party__fields'>
          {fields.map((field, index) => (
            <Field key={`${field.key}-${index}`} field={field} />
          ))}
        </div>
      )}
    </div>
  )
}

const PartyLabel: FC<{children: ReactNode}> = ({children}) => (
  <div className='cp-parties__label'>
    <span>{children}</span>
  </div>
)

export const ClassicParties: FC<{invoice: InvoicePrintModel}> = ({invoice}) => {
  const {status} = invoice
  const provisional =
    !!invoice.taxId && !status.taxIdFinal && status.isActive && status.code !== 'cancelled'
  return (
    <div className='cp-parties'>
      <PartyLabel>فروشنده</PartyLabel>
      <PartyBox party={invoice.seller} empty='مشخصات فروشنده ثبت نشده است.' />
      <div className='cp-box cp-side'>
        {invoice.invoiceNumber && (
          <SideRow label='شماره صورتحساب'>
            <Code value={invoice.invoiceNumber} />
          </SideRow>
        )}
        <SideRow label='تاریخ و زمان صدور صورتحساب'>
          <bdi className='cp-date'>{invoice.issuedAt}</bdi>
        </SideRow>
        {invoice.article9 && (
          <SideRow
            label={
              <>
                <span className='cp-badge'>ماده ۹</span> تاریخ و زمان ثبت صورتحساب
              </>
            }
          >
            <bdi className='cp-date'>{invoice.article9.registeredAt || '—'}</bdi>
          </SideRow>
        )}
        <SideRow label='کد پیگیری سامانه'>
          <Code value={invoice.trackingCode} />
        </SideRow>
        {invoice.internalSerial && (
          <SideRow label='سریال صورتحساب'>
            <Code value={invoice.internalSerial} />
          </SideRow>
        )}
      </div>

      <PartyLabel>خریدار</PartyLabel>
      <PartyBox
        party={invoice.buyer}
        empty={
          invoice.invoiceType === 1
            ? 'مشخصات خریدار در این صورتحساب ثبت نشده است.'
            : 'در صورتحساب نوع ۲ ثبت مشخصات خریدار الزامی نیست و ثبت نشده است.'
        }
      />
      <div className='cp-box cp-side'>
        <SideRow label='شماره منحصر به فرد مالیاتی'>
          {invoice.taxId ? (
            <>
              <Code
                value={invoice.taxId}
                className={clsx({'cp-code--struck': status.code === 'superseded'})}
              />
              {provisional && <span className='cp-mark'>{PROVISIONAL_MARK}</span>}
            </>
          ) : (
            '—'
          )}
        </SideRow>
        <SideRow label='وضعیت'>
          {status.title}
          {status.isSandbox && <span className='cp-sandbox'>محیط آزمایشی</span>}
        </SideRow>
        <SideRow label='موضوع'>صورتحساب {invoice.subjectTitle}</SideRow>
        {invoice.referenceTaxId && (
          <SideRow label='شماره منحصر به فرد مالیاتی صورتحساب مرجع'>
            <Code value={invoice.referenceTaxId} />
          </SideRow>
        )}
      </div>
    </div>
  )
}

export const ClassicPattern: FC<{invoice: InvoicePrintModel; fields: ClassicField[]}> = ({
  invoice,
  fields,
}) => (
  <section className='cp-box cp-pattern'>
    {fields.length > 0 && (
      <div className='cp-pattern__grid'>
        {fields.map((field, index) => (
          <Field key={`${field.key}-${index}`} field={field} />
        ))}
      </div>
    )}
    {invoice.shippedGoods.length > 0 && (
      <div className='cp-pattern__shipped'>
        <span className='cp-field__label'>کالاهای حمل‌شده:</span>
        {invoice.shippedGoods.map((good, index) => (
          <span key={index} className='cp-pattern__good'>
            <Code value={good.stuffId} /> {good.title ? persianText(good.title) : ''}
          </span>
        ))}
      </div>
    )}
  </section>
)

// ---------------------------------------------------------------- rows and totals

/** Row number, goods ID and description: the settlement's place beside the totals. */
const SETTLEMENT_COLUMNS = 3

const cellClass = (column: PrintColumn) =>
  clsx('cp-cell', `cp-cell--${column.kind}`, `cp-col-${column.key}`)

const ColumnWidths: FC<{widths?: number[]}> = ({widths}) => {
  if (!widths || widths.length === 0) return null
  // Shares of the table, not pixels: every copy of the columns adds up to the sheet's width.
  const total = widths.reduce((sum, width) => sum + width, 0)
  return (
    <colgroup>
      {widths.map((width, index) => (
        <col key={index} style={{width: `${((width / total) * 100).toFixed(4)}%`}} />
      ))}
    </colgroup>
  )
}

const TotalCells: FC<{columns: PrintColumn[]; values: Record<string, string | null>}> = ({
  columns,
  values,
}) => (
  <>
    {columns.map((column) => (
      <td key={column.key} className={cellClass(column)}>
        {column.sum && <Num value={values[column.key]} />}
      </td>
    ))}
  </>
)

/**
 * The rows from..to under the repeated column heads. With totals, the foot adds the sheet's sum and
 * the running sum of every summable column («جمع این صفحه» and «جمع کل تا این صفحه»). The measuring
 * copy also lays out probeTotals in collapsed rows: they take no height but widen the columns to
 * hold every total the sheets print.
 */
export const ClassicItemsTable: FC<{
  invoice: InvoicePrintModel
  columns: PrintColumn[]
  widths?: number[]
  from: number
  to: number
  totals?: RowsTotals
  probeTotals?: Record<string, string | null>[]
}> = ({invoice, columns, widths, from, to, totals, probeTotals}) => {
  const labelSpan = totalsLabelSpan(columns)
  return (
    <div className='cp-items' data-measure='items-table'>
      <table className={clsx('cp-table cp-table--items', {'cp-table--fixed': !!widths})}>
        <ColumnWidths widths={widths} />
        <thead data-measure='items-head'>
          <tr>
            {columns.map((column) => {
              const unit = columnUnit(column)
              return (
                <th key={column.key} className={cellClass(column)} scope='col'>
                  <span className='cp-table__head'>
                    {column.title}
                    {unit && <span className='cp-table__unit'>({unit})</span>}
                  </span>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {invoice.items.slice(from, to).map((item) => (
            <tr key={item.row} data-measure='items-row'>
              {columns.map((column) => {
                const raw = column.value(item)
                const suffix = column.suffix?.(item)
                const secondary = column.secondary?.(item)
                return (
                  <td key={column.key} className={cellClass(column)}>
                    {column.kind === 'code' ? (
                      <Code value={raw} />
                    ) : column.kind === 'amount' ||
                      column.kind === 'number' ||
                      column.kind === 'rate' ? (
                      <bdi dir='ltr' className='cp-num'>
                        {formatCell(column, raw)}
                      </bdi>
                    ) : column.kind === 'text' ? (
                      <span className='cp-items__title'>{raw}</span>
                    ) : (
                      raw
                    )}
                    {suffix && <span className='cp-items__unit'>{suffix}</span>}
                    {secondary && <span className='cp-items__secondary'>{secondary}</span>}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
        {probeTotals && probeTotals.length > 0 && (
          <tbody className='cp-table__probe' aria-hidden='true'>
            {probeTotals.map((probe, index) => (
              <tr key={index} className='cp-table__total ip-table__total'>
                <th colSpan={labelSpan} />
                <TotalCells columns={columns.slice(labelSpan)} values={probe} />
              </tr>
            ))}
          </tbody>
        )}
        {totals && (
          <tfoot data-measure='items-foot'>
            {(['page', 'running'] as const).map((kind) => (
              <tr
                key={kind}
                className={clsx('cp-table__total ip-table__total', `cp-table__total--${kind}`)}
              >
                <th colSpan={labelSpan} scope='row' className='cp-table__total-label'>
                  {FOOT_LABELS[kind]}
                </th>
                <TotalCells columns={columns.slice(labelSpan)} values={totals[kind]} />
              </tr>
            ))}
          </tfoot>
        )}
      </table>
    </div>
  )
}

/**
 * Under the rows, on the rows' columns: «جمع کل» of every summable column, the invoice totals no
 * column prints, the invoice-wide discounts and the settlement beside them, then the total in words.
 */
export const ClassicTotals: FC<{
  invoice: InvoicePrintModel
  columns: PrintColumn[]
  widths?: number[]
  grand: Record<string, string | null>
  extraRows: TotalRow[]
}> = ({invoice, columns, widths, grand, extraRows}) => {
  const labelSpan = totalsLabelSpan(columns)
  const settlement = invoice.settlement
  const discounts = invoice.invoiceDiscounts
  // The settlement sits beside the totals, under the row number, goods ID and description columns
  // as on the earlier page; «جمع کل» takes the rest of the columns before the first total. Without
  // such a column left for the label, the settlement gets a line of its own under the totals.
  const sideSpan = settlement && labelSpan > SETTLEMENT_COLUMNS ? SETTLEMENT_COLUMNS : 0
  const rowSpan = 1 + extraRows.length + (discounts.length > 0 ? 1 : 0)
  const valueSpan = columns.length - sideSpan
  const settlementLines = settlement && (
    <>
      <div className='cp-totals__settlement-line'>
        <span className='cp-field__label'>نحوه تسویه:</span> <strong>{settlement.title}</strong>
      </div>
      {settlement.cashAmount && (
        <div className='cp-totals__settlement-line'>
          <span className='cp-field__label'>مبلغ پرداختی نقدی:</span>{' '}
          <strong>
            <Num value={settlement.cashAmount} /> ریال
          </strong>
        </div>
      )}
      {settlement.creditAmount && (
        <div className='cp-totals__settlement-line'>
          <span className='cp-field__label'>مبلغ نسیه:</span>{' '}
          <strong>
            <Num value={settlement.creditAmount} /> ریال
          </strong>
        </div>
      )}
    </>
  )
  return (
    <table className={clsx('cp-table cp-table--totals', {'cp-table--fixed': !!widths})}>
      <ColumnWidths widths={widths} />
      <tbody>
        <tr className='cp-totals__grand'>
          {sideSpan > 0 && (
            <td colSpan={sideSpan} rowSpan={rowSpan} className='cp-totals__settlement'>
              {settlementLines}
            </td>
          )}
          <th colSpan={labelSpan - sideSpan} scope='row' className='cp-totals__label'>
            جمع کل
          </th>
          <TotalCells columns={columns.slice(labelSpan)} values={grand} />
        </tr>
        {extraRows.map((row) => (
          <tr key={row.key} className='cp-totals__extra'>
            <th colSpan={valueSpan - 1} scope='row' className='cp-totals__extra-label'>
              {row.title}
            </th>
            <td className='cp-cell cp-cell--amount'>
              <bdi dir='ltr' className='cp-num'>
                {row.value}
              </bdi>
            </td>
          </tr>
        ))}
        {discounts.length > 0 && (
          <tr className='cp-totals__discounts'>
            <td colSpan={valueSpan}>
              <span className='cp-field__label'>
                تخفیف‌های کل صورتحساب (در ستون تخفیف ردیف‌ها لحاظ شده):
              </span>{' '}
              {discounts.map((discount, index) => (
                <span key={index} className='cp-totals__discount'>
                  {discount.title ? persianText(discount.title) : 'تخفیف'}{' '}
                  <strong>
                    <Num value={discount.amount} /> ریال
                  </strong>
                </span>
              ))}
            </td>
          </tr>
        )}
        {settlement && sideSpan === 0 && (
          <tr className='cp-totals__settlement-row'>
            <td colSpan={columns.length} className='cp-totals__settlement'>
              {settlementLines}
            </td>
          </tr>
        )}
        {invoice.totals.totalInWords && (
          <tr className='cp-totals__words'>
            <td colSpan={columns.length}>
              <span className='cp-field__label'>مبلغ کل صورتحساب به حروف:</span>{' '}
              <strong>{invoice.totals.totalInWords}</strong>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  )
}

// ---------------------------------------------------------------- payments, notes, notices, signatures

export const ClassicPayments: FC<{
  invoice: InvoicePrintModel
  from: number
  to: number
  continued?: boolean
}> = ({invoice, from, to, continued}) => (
  <section className='cp-box cp-payments'>
    <div className='cp-box__title' data-measure='payments-title'>
      {continued ? 'پرداخت‌ها (ادامه)' : 'پرداخت‌ها'}
    </div>
    <table className='cp-table cp-table--payments'>
      <thead data-measure='payments-head'>
        <tr>
          <th scope='col'>ردیف</th>
          <th scope='col'>تاریخ و زمان پرداخت</th>
          <th scope='col'>روش پرداخت</th>
          <th scope='col'>شماره پیگیری/شماره مرجع</th>
          <th scope='col'>شماره کارت پرداخت‌کننده</th>
          <th scope='col'>مبلغ پرداختی (ریال)</th>
        </tr>
      </thead>
      <tbody>
        {invoice.payments.slice(from, to).map((payment, index) => (
          <tr key={from + index} data-measure='payments-row'>
            <td>{from + index + 1}</td>
            <td>
              <bdi className='cp-date'>{payment.paidAt}</bdi>
            </td>
            <td>{payment.methodTitle}</td>
            <td>{payment.traceNumber ? <Code value={payment.traceNumber} /> : '—'}</td>
            <td>{payment.cardNumber ? <Code value={payment.cardNumber} /> : '—'}</td>
            <td className='cp-cell--amount'>
              <Num value={payment.amount} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    {to === invoice.payments.length && invoice.paymentsTotal && (
      <div className='cp-payments__summary' data-measure='payments-summary'>
        <span>
          جمع پرداخت‌ها: <Num value={invoice.paymentsTotal} /> ریال
        </span>
        {invoice.remaining && (
          <span>
            باقیمانده صورتحساب: <Num value={invoice.remaining} /> ریال
          </span>
        )}
      </div>
    )}
  </section>
)

export const ClassicNotes: FC<{
  lines: ClassicNoteLine[]
  title: string
  from: number
  to: number
  continued?: boolean
}> = ({lines, title, from, to, continued}) => (
  <section className='cp-box cp-notes'>
    <div className='cp-box__title' data-measure='notes-title'>
      {continued ? `${title} (ادامه)` : title}
    </div>
    <div className='cp-notes__body'>
      {lines.slice(from, to).map((line, index) =>
        line.kind === 'attachments' ? (
          <p key={from + index} className='cp-notes__line' data-measure='notes-line'>
            <span className='cp-field__label'>ضمائم:</span>{' '}
            {line.files.map((file, fileIndex) => (
              <span key={fileIndex} className='cp-notes__file'>
                {fileIndex > 0 && '، '}
                <a
                  href={getAttachmentFileUrl(file.source)}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <bdi>{file.name}</bdi>
                </a>
              </span>
            ))}
          </p>
        ) : (
          <p
            key={from + index}
            className={clsx('cp-notes__line', {'cp-notes__line--gap': !line.text})}
            data-measure='notes-line'
          >
            {line.text || ' '}
          </p>
        )
      )}
    </div>
  </section>
)

export const ClassicNotices: FC<{invoice: InvoicePrintModel}> = ({invoice}) => (
  <section className='cp-notices'>
    {notices(invoice).map((notice, index) => (
      <p key={index}>{notice}</p>
    ))}
  </section>
)

/**
 * The seller's stamp and signature at the template's height (a fixed box, so the measure does not
 * wait for the picture and a small picture is drawn as large as the box), and the buyer's box when
 * the template keeps it.
 */
export const ClassicSignatures: FC<{
  invoice: InvoicePrintModel
  showSignature: boolean
  heightMm: number
  buyerBox: boolean
  compact?: boolean
}> = ({invoice, showSignature, heightMm, buyerBox, compact}) => (
  <section
    className={clsx('cp-signatures', {'cp-signatures--compact': compact})}
    style={{'--cp-sign-mm': `${heightMm}mm`} as CSSProperties}
  >
    <div className='cp-signatures__box'>
      <div className='cp-signatures__title'>مهر و امضای فروشنده</div>
      <div className='cp-signatures__space'>
        {showSignature && invoice.signatureUrl && (
          <img
            className='cp-signatures__image'
            src={invoice.signatureUrl}
            alt='مهر و امضای فروشنده'
          />
        )}
      </div>
    </div>
    {buyerBox && (
      <div className='cp-signatures__box'>
        <div className='cp-signatures__title'>مهر و امضای خریدار</div>
        <div className='cp-signatures__space' />
      </div>
    )}
  </section>
)
